// admin-web/src/router/index.js
import { createRouter, createWebHistory } from "vue-router";
import SignInView from "@/views/SignInView.vue";
import SignupView from "@/views/SignupView.vue";
import ClassBookingView from "@/views/ClassBookingView.vue";
import NotFoundView from "@/views/NotFoundView.vue";
import RecordsView from "@/views/RecordsView.vue";
import FreeTrialView from "@/views/FreeTrialView.vue";
import ForgotPasswordView from "@/views/ForgotPasswordView.vue";
import ResetPasswordView from "@/views/ResetPasswordView.vue";
import ProfileView from "@/views/ProfileView.vue";
import MainLayout from "@/components/MainLayout.vue";
import InviteView from "@/views/InviteView.vue";
import MemberListView from "@/views/MemberListView.vue";
import NewMembersView from "@/views/NewMembersView.vue";
import TrendsView from "@/views/TrendsView.vue";
import FreeTrialRequestsView from "@/views/FreeTrialRequestsView.vue";
import RealtimeHrView from "@/views/RealtimeHrView.vue";
import DeviceManagementView from "@/views/DeviceManagementView.vue";
import RewardsView from "@/views/RewardsView.vue";
import DailyProgramsView from "@/views/DailyProgramsView.vue";
import MachineRecordsView from "@/views/MachineRecordsView.vue";

import { getUser, getMeOnce, clearSession } from "@/lib/session";
import { canAccessRoute, isCoachRole, TRIAL_CRM_PATH } from "@/lib/adminAccess";

const routes = [
  // 인증이 필요한 메인 레이아웃
  {
    path: "/",
    component: MainLayout,
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: "/booking" },
      {
        path: "booking",
        name: "ClassBooking",
        component: ClassBookingView,
      },
      {
        // R9 운동 프로그램(날짜·타입별 안내 문장·내용, R10 카디오 포커스·웨이트 포커스, record-daily-design.md 0절 7·12·16). ?date=&type=로 바로 연다
        path: "programs",
        name: "DailyPrograms",
        component: DailyProgramsView,
      },
      {
        path: "profile",
        name: "Profile",
        component: ProfileView,
      },
      {
        path: "profile/:user_id",
        name: "MemberProfile",
        component: ProfileView,
      },
      {
        path: "records",
        name: "Records",
        component: RecordsView,
      },
      {
        path: "records/:user_id",
        name: "MemberRecords",
        component: RecordsView,
      },
      {
        path: "invite",
        name: "Invite",
        component: InviteView,
      },
      {
        path: "member-list",
        name: "MemberList",
        component: MemberListView,
      },
      {
        path: "new-members",
        name: "NewMembers",
        component: NewMembersView,
      },
      // 회원권 추세 (docs/architecture/admin-trends-design.md 4절)
      {
        path: "trends",
        name: "Trends",
        component: TrendsView,
      },
      {
        path: "free-trial-requests",
        name: "FreeTrialRequests",
        component: FreeTrialRequestsView,
        meta: { staffOnly: true },
      },
      {
        path: "realtime-hr",
        name: "RealtimeHr",
        component: RealtimeHrView,
      },
      {
        // R7 머신 기록 입력·수정(docs/architecture/app-v2/R6b-R7.md 2.4 위치 2). ?date=YYYY-MM-DD&class_id=로 그 수업을 바로 연다
        path: "machine-records",
        name: "MachineRecords",
        component: MachineRecordsView,
      },
      {
        path: "device-management",
        name: "DeviceManagement",
        component: DeviceManagementView,
      },
      {
        // R4 연속 보상 적용(결정 R4-5)
        path: "rewards",
        name: "Rewards",
        component: RewardsView,
      },
    ],
  },
  // 인증이 필요하지 않은 페이지들
  { path: "/signin", name: "SignIn", component: SignInView },
  { path: "/signup", name: "Signup", component: SignupView },
  { path: "/free-trial", name: "FreeTrial", component: FreeTrialView },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: ForgotPasswordView,
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: ResetPasswordView,
  },
  { path: "/:pathMatch(.*)*", name: "NotFound", component: NotFoundView },
];

// 뒤로·앞으로 가기는 그 화면에서 보던 스크롤 위치로 돌아간다. 목록(신규회원·회원관리)과 회원 상세는 다시 읽어 그리므로 그 높이가 될 때까지
// (최대 2초) 기다렸다가 옮긴다. 4차 리뷰: 늘 맨 위로 가서, 신규회원 90일 목록의 13번째 회원을 보고 돌아오면 다시 찾아 내려가야 했다(main부터)
// 기다리는 사이 다른 화면으로 옮겼거나(새 이동이 시작됨) 코치가 직접 스크롤·클릭·키를 누르면 되돌리지 않는다. 5차 리뷰: 목록이 짧아져
// 그 높이가 되지 않는 사이 다음 회원을 열면, 2초 뒤 그 회원 상세가 맨 아래로 뛰어 이름·특이사항이 화면 밖으로 나갔다
const SCROLL_RESTORE_WAIT_MS = 2000;
const SCROLL_RESTORE_CANCEL_EVENTS = [
  "wheel",
  "touchmove",
  "keydown",
  "mousedown",
];
let navigationCount = 0; // router.beforeEach마다 늘어난다
// 회원권 추세 안에서 탭·기간·거르기 버튼이 주소의 쿼리만 바꾸는 이동(router.replace)은 보던 자리를 그대로 둔다(리뷰 3차: 거르기를
// 누를 때마다 맨 위로 가서 390px에서 거른 목록이 첫 화면 아래로 밀렸다). vue-router는 replace한 항목의 history.state에
// replaced: true를 둔다. 사이드메뉴로 다시 들어온 /trends(push, 새 방문 기록 항목)는 지금처럼 맨 위에서 시작한다.
// 머신 기록 화면(R7)의 날짜·수업 고르기도 쿼리만 replace한다. 맨 위로 가면 수업 목록 아래에 그리는 폼이 화면 밖으로 더 밀렸다
// (390px에서 수업을 누르면 폼 머리만 화면 바닥에 남음, R7 전체 리뷰 1차)
const QUERY_REPLACE_KEEPS_SCROLL = new Set(["Trends", "MachineRecords"]);
function keepsScrollOnQueryReplace(to, from) {
  if (to.name !== from.name || !QUERY_REPLACE_KEEPS_SCROLL.has(to.name))
    return false;
  if (to.path !== from.path) return false;
  try {
    return !!(window.history.state && window.history.state.replaced);
  } catch (e) {
    return false;
  }
}
function restoreScroll(to, from, savedPosition) {
  if (!savedPosition)
    return keepsScrollOnQueryReplace(to, from) ? false : { top: 0 };
  const navigation = navigationCount;
  const started = Date.now();
  let userMoved = false;
  const onUserInput = () => {
    userMoved = true;
  };
  SCROLL_RESTORE_CANCEL_EVENTS.forEach((type) =>
    window.addEventListener(type, onUserInput, { capture: true, passive: true })
  );
  return new Promise((resolve) => {
    const finish = (position) => {
      SCROLL_RESTORE_CANCEL_EVENTS.forEach((type) =>
        window.removeEventListener(type, onUserInput, { capture: true })
      );
      resolve(position);
    };
    const attempt = () => {
      // false면 vue-router가 스크롤하지 않는다
      if (navigation !== navigationCount || userMoved) {
        finish(false);
        return;
      }
      const room = document.documentElement.scrollHeight - window.innerHeight;
      if (
        room >= savedPosition.top ||
        Date.now() - started > SCROLL_RESTORE_WAIT_MS
      ) {
        finish(savedPosition);
      } else {
        setTimeout(attempt, 50);
      }
    };
    attempt();
  });
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior: restoreScroll,
});

router.beforeEach(async (to) => {
  // 기다리던 스크롤 되돌리기를 그만두게 한다(restoreScroll)
  navigationCount++;

  // 인증 불필요한 페이지는 통과
  if (!to.meta?.requiresAuth) return true;

  // 사용자 정보 가져오기
  const me = getUser() ?? (await getMeOnce());

  // 로그인 안 되어 있으면 로그인 페이지로
  if (!me) {
    clearSession();
    return { path: "/signin", query: { redirect: to.fullPath } };
  }

  if (!canAccessRoute(me.role, to.name)) {
    // 코치는 체험 고객 관리에만 접근한다. 관리자 화면 주소를 직접 열어도
    // 유효한 세션을 지우지 않고 허용된 첫 화면으로 안전하게 돌려보낸다.
    if (isCoachRole(me.role)) return TRIAL_CRM_PATH;

    clearSession();
    return { name: "NotFound" };
  }

  return true;
});

export default router;
